import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { Button } from '../components/Ui';
import { Reveal } from '../components/Motion';
import api from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { APP, VERTICALS, PARTNER_TYPES } from '../constants';
import { colors } from '../theme';

/**
 * Public front page of kaamwala.com.
 *
 * Everything below the fold is the same catalogue the app shows - the category
 * grid is pulled live from /home, so the marketing page and the product never
 * drift apart. Every call to action lands in the same signed-in workflow.
 */

const FEATURES = [
  {
    icon: '🛠️',
    title: 'Home & local services',
    text: 'Electrician, plumber, AC service, cleaning, carpenter, pest control — pick a slot, get a fixed price, track the job.',
    color: colors.primary,
  },
  {
    icon: '🛒',
    title: 'Shops & daily essentials',
    text: 'Order groceries, medicines and food from shops in your own market. A separate cart per shop, delivery or pickup.',
    color: '#16A34A',
  },
  {
    icon: '🚕',
    title: 'Rides & parcels',
    text: 'Bike taxi, auto, e-rickshaw, taxi and cab with an upfront fare — plus pick-and-drop courier across the city.',
    color: colors.accent,
  },
  {
    icon: '💼',
    title: 'Jobs near you',
    text: 'Full time, part time, contract and daily wage openings. Apply in one tap and track every application.',
    color: '#7C3AED',
  },
  {
    icon: '🚗',
    title: 'Buy & sell vehicles',
    text: 'List your car, bike or scooter free, or browse verified listings from owners and dealers around you.',
    color: '#0891B2',
  },
  {
    icon: '✨',
    title: 'Your Desire',
    text: 'Need something we do not list yet? Describe it and our team finds the right person near you.',
    color: '#DB2777',
  },
];

const STEPS = [
  { n: '1', title: 'Tell us what you need', text: 'Search, or pick a category from the 48 we cover.' },
  { n: '2', title: 'Choose a partner', text: 'Compare ratings, prices and distance from verified local businesses.' },
  { n: '3', title: 'Book or order', text: 'Pick a slot or fill your cart. Pay online or after the work is done.' },
  { n: '4', title: 'Track it live', text: 'Watch the status timeline, share the start OTP, then rate the partner.' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isPartner } = useAuth();
  const { location } = useApp();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.app.home({ lat: location.latitude, lng: location.longitude })
      .then((res) => setCategories((res.data && res.data.categories) || []))
      .catch(() => setCategories([]));
  }, [location.latitude, location.longitude]);

  const goToApp = () => navigate(isPartner ? '/partner' : '/home');
  const start = () => (user ? goToApp() : navigate('/get-started'));

  return (
    <div className="landing">
      {/* ------------------------------- nav ------------------------------- */}
      <header className="landing-nav">
        <div className="landing-inner row-between" style={{ height: 68 }}>
          <Link to="/" className="row" style={{ gap: 10 }}>
            <Logo size={38} />
            <span style={{ fontWeight: 900, fontSize: 19, letterSpacing: 0.6, color: colors.primary }}>
              {APP.name}
            </span>
          </Link>

          <nav className="row hide-sm" style={{ gap: 26 }}>
            <a href="#services" className="t-body" style={{ fontWeight: 600 }}>Services</a>
            <a href="#how" className="t-body" style={{ fontWeight: 600 }}>How it works</a>
            <a href="#partners" className="t-body" style={{ fontWeight: 600 }}>For partners</a>
          </nav>

          <div className="row" style={{ gap: 10 }}>
            {user ? (
              <Button title="Open my dashboard" onClick={goToApp} size="sm" />
            ) : (
              <>
                <Button title="Log in" variant="light" size="sm" onClick={() => navigate('/login?role=user')} />
                <Button title="Get started" size="sm" onClick={() => navigate('/get-started')} />
              </>
            )}
          </div>
        </div>
      </header>

      {/* ------------------------------- hero ------------------------------ */}
      <section className="landing-section" style={{ paddingTop: 62 }}>
        <div className="hero-grid">
          <Reveal>
            <span
              className="row"
              style={{
                display: 'inline-flex', gap: 8, background: colors.primaryLight,
                color: colors.primary, padding: '7px 14px', borderRadius: 999,
                fontWeight: 700, fontSize: 12.5, marginBottom: 20,
              }}
            >
              ⚡ {APP.motto}
            </span>

            <h1 className="hero-title">
              Every local service,
              <br />
              <span style={{ color: colors.primary }}>one place.</span>
            </h1>

            <p className="hero-sub">
              KAAMWALA connects you with shops, service providers, professionals, drivers,
              recruiters and vehicle sellers around you. Book a service, order from a local shop,
              hire a cab, find a job — and track all of it in one account.
            </p>

            <div className="row wrap" style={{ gap: 12, marginTop: 28 }}>
              <Button title={user ? 'Open my dashboard' : 'Book a service'} icon="⚡" size="lg" onClick={start} />
              <Button
                title="Register your business"
                variant="outline"
                size="lg"
                onClick={() => navigate(user ? '/sell' : '/login?role=partner')}
              />
            </div>

            <div className="row wrap" style={{ gap: 22, marginTop: 30 }}>
              {[
                ['48', 'service categories'],
                ['11', 'kinds of partner'],
                ['1', 'account for everything'],
              ].map(([value, label]) => (
                <div key={label}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: colors.ink }}>{value}</div>
                  <div className="t-small">{label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={140}>
            <div className="hero-art">
              <div className="phone-frame">
                <div style={{ background: colors.primary, padding: '16px 16px 14px', color: '#fff' }}>
                  <div className="row" style={{ gap: 10 }}>
                    <Logo size={30} />
                    <div className="grow">
                      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1, opacity: 0.75 }}>
                        DELIVERING TO
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{location.label || location.city} ▾</div>
                    </div>
                    <span style={{ fontSize: 16 }}>🔔</span>
                  </div>
                  <div
                    className="row"
                    style={{
                      background: '#fff', borderRadius: 12, padding: '11px 12px',
                      marginTop: 12, gap: 8,
                    }}
                  >
                    <span>🔍</span>
                    <span className="t-small" style={{ color: colors.faint }}>
                      Search services, shops, jobs…
                    </span>
                  </div>
                  <div className="row no-bar" style={{ gap: 7, marginTop: 12, overflowX: 'auto' }}>
                    {VERTICALS.slice(0, 4).map((v, i) => (
                      <span
                        key={v.key}
                        style={{
                          flex: '0 0 auto', padding: '7px 12px', borderRadius: 999, fontSize: 12,
                          fontWeight: i === 0 ? 800 : 600,
                          background: i === 0 ? '#fff' : 'rgba(255,255,255,.18)',
                          color: i === 0 ? colors.primary : '#fff',
                        }}
                      >
                        {v.icon} {v.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ padding: 16, background: colors.bg }}>
                  <div className="h3" style={{ fontSize: 15 }}>Hi there 👋</div>
                  <div className="t-small" style={{ marginBottom: 12 }}>What do you need done today?</div>

                  <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    {(categories.length ? categories.slice(0, 8) : VERTICALS.slice(1, 7)).map((c, i) => (
                      <div key={c._id || c.key} style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            width: '100%', aspectRatio: '1', borderRadius: 14,
                            background: `${c.color || colors.primary}18`,
                            display: 'grid', placeItems: 'center', fontSize: 21,
                          }}
                        >
                          {c.icon || '📁'}
                        </div>
                        <div className="t-tiny clamp-1" style={{ marginTop: 4 }}>
                          {c.name || c.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="row"
                    style={{
                      marginTop: 14, background: '#fff', border: `1px solid ${colors.line}`,
                      borderRadius: 14, padding: 11, gap: 10,
                    }}
                  >
                    <span
                      style={{
                        width: 38, height: 38, borderRadius: 10, background: colors.successBg,
                        display: 'grid', placeItems: 'center', fontSize: 17,
                      }}
                    >
                      ✅
                    </span>
                    <div className="grow">
                      <div className="t-title" style={{ fontSize: 13 }}>AC service &amp; gas top-up</div>
                      <div className="t-tiny">In progress · share OTP 4821</div>
                    </div>
                    <span className="t-title" style={{ fontSize: 13 }}>₹1,499</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------------------- categories --------------------------- */}
      {categories.length ? (
        <section style={{ background: colors.bg, borderTop: `1px solid ${colors.line}` }}>
          <div className="landing-section" style={{ paddingTop: 54, paddingBottom: 54 }}>
            <div className="row-between wrap" style={{ marginBottom: 22 }}>
              <div>
                <h2 className="h2">Browse by category</h2>
                <p className="t-small" style={{ marginTop: 4 }}>
                  {categories.length} live categories — the same ones inside the app.
                </p>
              </div>
              <Button title="See all services" variant="light" onClick={start} />
            </div>

            <div className="grid grid-cat">
              {categories.slice(0, 24).map((c, i) => (
                <Reveal key={c._id} delay={Math.min(i * 22, 260)}>
                  <button
                    type="button"
                    onClick={start}
                    className="press"
                    style={{
                      background: '#fff', border: `1px solid ${colors.line}`, borderRadius: 16,
                      padding: '14px 8px', width: '100%',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                    }}
                  >
                    <span
                      style={{
                        width: 46, height: 46, borderRadius: 13, fontSize: 22,
                        background: `${c.color || colors.primary}18`,
                        display: 'grid', placeItems: 'center',
                      }}
                    >
                      {c.icon || '📁'}
                    </span>
                    <span className="t-tiny clamp-2" style={{ color: colors.ink2, textAlign: 'center', lineHeight: 1.25 }}>
                      {c.name}
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ----------------------------- features ---------------------------- */}
      <section id="services" className="landing-section">
        <div style={{ maxWidth: 620, marginBottom: 34 }}>
          <h2 className="h1" style={{ fontSize: 34 }}>Six products. One account.</h2>
          <p className="t-body" style={{ color: colors.muted, marginTop: 10, lineHeight: 1.65 }}>
            Everything the KAAMWALA app does works here in your browser too — same partners,
            same prices, same live tracking.
          </p>
        </div>

        <div className="grid grid-cards">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 60}>
              <div className="feature-card">
                <span
                  style={{
                    width: 50, height: 50, borderRadius: 14, fontSize: 24,
                    background: `${f.color}16`, display: 'grid', placeItems: 'center',
                  }}
                >
                  {f.icon}
                </span>
                <h3 className="h3" style={{ marginTop: 14 }}>{f.title}</h3>
                <p className="t-small" style={{ marginTop: 6, lineHeight: 1.6 }}>{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------------------- how it works -------------------------- */}
      <section id="how" style={{ background: colors.bg, borderTop: `1px solid ${colors.line}` }}>
        <div className="landing-section">
          <h2 className="h1" style={{ fontSize: 34, marginBottom: 8 }}>How KAAMWALA works</h2>
          <p className="t-body" style={{ color: colors.muted, marginBottom: 34 }}>
            Book ⚡ Track ⚡ Get it done — in four steps.
          </p>

          <div className="grid grid-2">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="card sh-sm" style={{ height: '100%' }}>
                  <span
                    style={{
                      width: 34, height: 34, borderRadius: 17, background: colors.primary,
                      color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800,
                    }}
                  >
                    {s.n}
                  </span>
                  <h3 className="h3" style={{ marginTop: 12 }}>{s.title}</h3>
                  <p className="t-small" style={{ marginTop: 5, lineHeight: 1.6 }}>{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="stat-band" style={{ marginTop: 34 }}>
            {[
              ['🔐', 'WhatsApp OTP login', 'No passwords. One number, one account.'],
              ['💳', 'Pay how you like', 'Cash on delivery, or UPI / card / netbanking online.'],
              ['📍', 'Live status timeline', 'Every order, booking and ride tracked end to end.'],
              ['⭐', 'Ratings you can trust', 'Reviews only from customers who actually booked.'],
            ].map(([icon, title, text]) => (
              <div key={title}>
                <div style={{ fontSize: 24 }}>{icon}</div>
                <div style={{ fontWeight: 800, marginTop: 8 }}>{title}</div>
                <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 4, lineHeight: 1.55 }}>{text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------- for partners -------------------------- */}
      <section id="partners" className="landing-section">
        <div className="hero-grid">
          <Reveal>
            <span
              style={{
                display: 'inline-block', background: colors.accentLight, color: colors.accentDark,
                padding: '7px 14px', borderRadius: 999, fontWeight: 700, fontSize: 12.5,
              }}
            >
              🏪 For businesses
            </span>
            <h2 className="h1" style={{ fontSize: 34, marginTop: 18 }}>
              Grow your business with KAAMWALA
            </h2>
            <p className="t-body" style={{ color: colors.muted, marginTop: 12, lineHeight: 1.65 }}>
              Shops, service providers, professionals, drivers, couriers and recruiters — list your
              work free and start receiving customers from your own neighbourhood. Manage orders,
              bookings, prices and stock from one dashboard.
            </p>

            <div className="col" style={{ gap: 12, marginTop: 22 }}>
              {[
                ['📈', 'More customers', 'Get discovered by people searching in your own area.'],
                ['💸', 'Zero listing fee', 'Register your shop or skill and start listing for free.'],
                ['📱', 'One dashboard', 'Orders, bookings, catalogue and earnings in one place.'],
                ['⭐', 'Build your reputation', 'Collect ratings and reviews from happy customers.'],
              ].map(([icon, title, text]) => (
                <div key={title} className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      width: 40, height: 40, borderRadius: 12, background: colors.primaryLight,
                      display: 'grid', placeItems: 'center', fontSize: 19, flex: '0 0 auto',
                    }}
                  >
                    {icon}
                  </span>
                  <span>
                    <span className="t-title" style={{ display: 'block' }}>{title}</span>
                    <span className="t-small" style={{ display: 'block', marginTop: 2 }}>{text}</span>
                  </span>
                </div>
              ))}
            </div>

            <Button
              title="Register my business"
              icon="🚀"
              size="lg"
              style={{ marginTop: 26 }}
              onClick={() => navigate(user ? '/sell' : '/login?role=partner')}
            />
          </Reveal>

          <Reveal delay={120}>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
              {PARTNER_TYPES.slice(0, 12).map((t, i) => (
                <div
                  key={t.value}
                  className="card sh-sm reveal"
                  style={{ textAlign: 'center', padding: 16, animationDelay: `${i * 40}ms` }}
                >
                  <div style={{ fontSize: 24 }}>{t.icon}</div>
                  <div className="t-tiny" style={{ marginTop: 6, color: colors.ink2, lineHeight: 1.3 }}>
                    {t.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------------------------------- cta ------------------------------- */}
      <section className="landing-section" style={{ paddingTop: 0 }}>
        <div
          style={{
            background: `linear-gradient(120deg, ${colors.primary}, ${colors.primaryDark})`,
            borderRadius: 26, padding: '48px 34px', textAlign: 'center', color: '#fff',
          }}
        >
          <div style={{ fontSize: 44 }}>⚡</div>
          <h2 className="h1" style={{ color: '#fff', fontSize: 32, marginTop: 10 }}>
            Ready when you are
          </h2>
          <p style={{ opacity: 0.88, marginTop: 10, maxWidth: 480, marginInline: 'auto', lineHeight: 1.6 }}>
            Sign in with your mobile number — the OTP arrives on WhatsApp. It takes about
            thirty seconds.
          </p>
          <div className="row wrap" style={{ justifyContent: 'center', gap: 12, marginTop: 26 }}>
            <Button title={user ? 'Open my dashboard' : 'Get started free'} variant="accent" size="lg" onClick={start} />
            <Button
              title="I provide a service"
              variant="light"
              size="lg"
              onClick={() => navigate(user ? '/sell' : '/login?role=partner')}
            />
          </div>
        </div>
      </section>

      {/* ------------------------------ footer ----------------------------- */}
      <footer className="landing-footer">
        <div className="landing-inner">
          <div className="grid grid-2" style={{ gap: 30 }}>
            <div>
              <div className="row" style={{ gap: 10 }}>
                <Logo size={36} />
                <span style={{ fontWeight: 900, fontSize: 18, letterSpacing: 0.6, color: '#fff' }}>
                  {APP.name}
                </span>
              </div>
              <p style={{ marginTop: 12, lineHeight: 1.7, maxWidth: 320, fontSize: 13 }}>
                {APP.tagline}. A local marketplace connecting customers with shops, service
                providers, professionals, drivers, recruiters and vehicle sellers.
              </p>
            </div>

            <div>
              <div style={{ color: '#fff', fontWeight: 800, marginBottom: 10 }}>Customers</div>
              <div className="col" style={{ gap: 8, fontSize: 13 }}>
                <Link to="/get-started">Book a service</Link>
                <Link to="/get-started">Order from shops</Link>
                <Link to="/get-started">Book a ride or parcel</Link>
                <Link to="/get-started">Find jobs</Link>
                <Link to="/get-started">Buy &amp; sell vehicles</Link>
              </div>
            </div>

            <div>
              <div style={{ color: '#fff', fontWeight: 800, marginBottom: 10 }}>Partners</div>
              <div className="col" style={{ gap: 8, fontSize: 13 }}>
                <Link to="/login?role=partner">Register your business</Link>
                <Link to="/login?role=partner">Partner login</Link>
                <a href={`tel:${APP.supportPhone}`}>Help &amp; support</a>
              </div>
            </div>
          </div>

          <div
            className="row-between wrap"
            style={{
              marginTop: 34, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,.12)',
              fontSize: 12.5, gap: 10,
            }}
          >
            <span>© {new Date().getFullYear()} {APP.name}. All rights reserved.</span>
            <span className="row" style={{ gap: 4 }}>
              Support:
              <a
                href={`tel:${APP.supportPhone}`}
                style={{ minHeight: 34, display: 'inline-flex', alignItems: 'center' }}
              >
                {APP.supportPhone}
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
